/**
 * @file Steps 步骤条
 * @description 水平步骤流程；当前 / 完成 / 等待。
 *
 * @whenToUse 多步表单、审批流示意。
 * @whenNotToUse 页签切换（用 Tabs / Segmented）。
 */

import type { ReactNode } from 'react'
import { Check } from 'lucide-react'
import { cn } from '@/lib/cn.ts'

export type StepItem = {
  key: string
  title: ReactNode
  description?: ReactNode
}

export type StepsProps = {
  items: StepItem[]
  /** 当前步骤索引（从 0） */
  current: number
  className?: string
}

export function Steps({ items, current, className }: StepsProps) {
  return (
    <ol className={cn('flex w-full items-start gap-2', className)}>
      {items.map((item, index) => {
        const done = index < current
        const active = index === current
        return (
          <li
            key={item.key}
            className={cn(
              'flex min-w-0 flex-1 items-start gap-2',
              index < items.length - 1 && 'pr-2',
            )}
          >
            <div className="flex min-w-0 flex-1 flex-col gap-1.5">
              <div className="flex items-center gap-2">
                <span
                  className={cn(
                    'inline-flex size-7 shrink-0 items-center justify-center rounded-full border text-xs font-medium transition-opacity duration-200',
                    done &&
                      'border-primary bg-primary text-primary-foreground',
                    active &&
                      'border-primary bg-raised text-primary',
                    !done &&
                      !active &&
                      'border-border bg-raised text-muted',
                  )}
                >
                  {done ? <Check className="size-3.5" aria-hidden /> : index + 1}
                </span>
                <span
                  className={cn(
                    'truncate text-sm',
                    active || done
                      ? 'font-medium text-foreground'
                      : 'text-muted',
                  )}
                >
                  {item.title}
                </span>
              </div>
              {item.description ? (
                <p className="pl-9 text-xs text-muted">{item.description}</p>
              ) : null}
            </div>
            {index < items.length - 1 ? (
              <div
                className={cn(
                  'mt-3.5 hidden h-px min-w-4 flex-1 sm:block',
                  done ? 'bg-primary' : 'bg-border',
                )}
                aria-hidden
              />
            ) : null}
          </li>
        )
      })}
    </ol>
  )
}
